import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { Stack, Typography } from "@mui/material";
import { useRequestThanksToYouWithdrawalMutation } from "Api/Mutations/Lead/useRequestThanksToYouWithdrawalMutation";
import { OptOutOutcomeColumn } from "Components/Affiliate/ThanksToYou/OptOutOutcomeColumn";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { ConfirmDialog } from "Components/Shared/Dialogs/ConfirmDialog";
import { InfoBox } from "Components/Shared/InfoBox/InfoBox";
import { PaperTitle } from "Components/Shared/PaperTitle";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { useConfirmDialog } from "Hooks/useConfirmDialog";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { getApiErrorStatus } from "Infrastructure/Api/ApiUtils";
import { HttpStatusCode } from "Models/HttpStatusCodes";
import { Resources, useResource } from "Translations/Resources";
import { formatDate } from "Utils/DateUtils";
import { toast } from "sonner";

const RESOURCES = Resources.Components.Affiliate.ThanksToYou.Participation;

// The Resources leaves are key objects, not strings, so each line carries its own string id for
// the React key.
const LOSE_TRANSLATION_KEYS = [
  { id: "referrals", translationKey: RESOURCES.OptOutDialogLoseReferrals },
  { id: "inviter", translationKey: RESOURCES.OptOutDialogLoseInviter },
];

const KEEP_TRANSLATION_KEYS = [
  { id: "rewards", translationKey: RESOURCES.OptOutDialogKeepRewards },
  { id: "account", translationKey: RESOURCES.OptOutDialogKeepAccount },
];

/**
 * Bottom of the Díky Tobě section (ZS-1623), rendered only for a person in the program. Leaving is
 * irreversible, so it takes two steps: a confirmation that spells out the consequences, then an
 * e-mail whose link completes it. Until the link is used nothing changes, and the person can ask for
 * the e-mail again (which revokes the earlier link).
 */
export const ThanksToYouParticipationSection = () => {
  const { t } = useResource();
  const user = useLoggedInUser();
  const { isOpen, open, handleResult } = useConfirmDialog();
  // A second request within the cooldown is a 429: the first e-mail is on its way, no new one is sent.
  const handleRequestError = (error: unknown) => {
    const isCooldown =
      getApiErrorStatus(error) === HttpStatusCode.TooManyRequests;
    toast.error(
      t(isCooldown ? RESOURCES.EmailCooldownError : RESOURCES.EmailSendError),
    );
  };
  const {
    mutate: requestWithdrawal,
    data: request,
    isPending,
  } = useRequestThanksToYouWithdrawalMutation({
    onSuccess: () => toast.success(t(RESOURCES.EmailSentToast)),
    onError: handleRequestError,
  });

  const handleRequestEmail = () => requestWithdrawal();

  return (
    <PrimaryPaper>
      <Stack gap={3}>
        <Stack gap={1}>
          <PaperTitle>{t(RESOURCES.Title)}</PaperTitle>
          <Typography>{t(RESOURCES.OptedInStatus)}</Typography>
        </Stack>
        {request ? (
          <Stack gap={2}>
            <Typography>
              {t(RESOURCES.EmailSent, {
                email: request.email,
                expiresAt: formatDate(request.expiresAt, true),
              })}
            </Typography>
            <PrimaryButton
              fullWidth
              variant="outlined"
              onClick={handleRequestEmail}
              isLoading={isPending}
            >
              {t(RESOURCES.ResendButton)}
            </PrimaryButton>
          </Stack>
        ) : (
          <PrimaryButton
            fullWidth
            variant="outlined"
            color="error"
            onClick={open}
            isLoading={isPending}
          >
            {t(RESOURCES.OptOutButton)}
          </PrimaryButton>
        )}
        <ConfirmDialog
          isOpen={isOpen}
          modalTitle={t(RESOURCES.OptOutDialogTitle)}
          modalContent={
            <Stack gap={2}>
              <InfoBox
                variant="warning"
                content={t(RESOURCES.OptOutDialogIrreversible)}
              />
              <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
                <OptOutOutcomeColumn
                  variant="lose"
                  title={t(RESOURCES.OptOutDialogLoseTitle)}
                  lines={LOSE_TRANSLATION_KEYS}
                />
                <OptOutOutcomeColumn
                  variant="keep"
                  title={t(RESOURCES.OptOutDialogKeepTitle)}
                  lines={KEEP_TRANSLATION_KEYS}
                />
              </Stack>
              {user && (
                <Stack direction="row" gap={1} alignItems="center">
                  <MailOutlineIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {t(RESOURCES.OptOutDialogEmailNote, { email: user.login })}
                  </Typography>
                </Stack>
              )}
            </Stack>
          }
          confirmButtonText={t(RESOURCES.OptOutDialogConfirm)}
          isDestructive
          handleResult={handleResult(handleRequestEmail)}
        />
      </Stack>
    </PrimaryPaper>
  );
};
